/**
 * data/images.ts — image slots for the site.
 * "Tasteful stock for now, swap later": each slot points to a stock photo.
 * To use Grace Haven's own photography, drop files in /public/images and
 * change the URL here (e.g. '/images/hero.jpg'). The <Photo> component
 * shows a warm on-brand gradient if any image fails to load, so the site
 * never looks broken mid-swap.
 */
const U = (id: string, w = 1600) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

/**
 * HERO_VIDEO — default full-screen background video for the homepage hero.
 *
 * Points at `/hero.mp4` (drop the clip at `public/hero.mp4`). This is only the
 * DEFAULT — the live value is the editable `heroVideo` slot below, so the spa
 * can swap it from the admin Images tab (a Firebase Storage/CDN URL or another
 * site-relative path) with no redeploy. The hero falls back to the poster image
 * automatically if the video is missing, can't play, or the visitor prefers
 * reduced motion. Pick a calm, slow clip (spa setting / African wellness).
 */
export const HERO_VIDEO = '/hero.mp4';

export const IMG = {
  heroVideo: HERO_VIDEO, // editable homepage background video (URL or /path)
  hero: U('1540555700478-4be289fbecef'), // spa stones & candles
  heroWide: U('1540555700478-4be289fbecef', 2000), // full-screen poster
  spaPillar: U('1600334089648-b0d9d3028eb2', 1400), // massage / spa
  stayPillar: U('1611892440504-42a792e24d32', 1400), // bedroom
  philosophy: U('1600334129128-685c5582fd35', 1200), // massage table, warm
  massage: U('1544161515-4ab6ce6db874', 1000),
  facial: U('1570172619644-dfd03ed5d881', 1000),
  nails: U('1604654894610-df63bc536371', 1000),
  lounge: U('1571902943202-507ec2618e8f', 1200),
  oils: U('1556228578-8c89e6adf883', 1000),
  stones: U('1519823551278-64ac92734fb1', 1000),
  room: U('1611892440504-42a792e24d32', 1200), // bedroom
  suite: U('1582719478250-c89cae4dc85b', 1200),
  ctaBand: U('1512290923902-8a9f81dc236c', 1600),
  carPillar: U('1552519507-da3b142c6e3d', 1400), // luxury car — car-hire feature image
  // Per-room images — editable in their own right; default to the stock the
  // rooms shipped with (see data/accommodation.ts).
  roomCosy: U('1611892440504-42a792e24d32', 1200), // = room
  roomStandard: U('1582719478250-c89cae4dc85b', 1200), // = suite
  roomDeluxe: U('1611892440504-42a792e24d32', 1400), // = stayPillar
} as const;

export type ImageKey = keyof typeof IMG;

/**
 * Ordered, labelled catalogue for the admin Images editor. Only the slots
 * that appear on the public site are listed — each maps a friendly label and
 * a group so the editor can present them sensibly.
 */
export const IMAGE_KEYS: { key: ImageKey; label: string; group: string }[] = [
  { key: 'heroVideo', label: 'Hero background video', group: 'Hero' },
  { key: 'heroWide', label: 'Homepage hero', group: 'Hero' },
  { key: 'spaPillar', label: 'Spa feature', group: 'Home' },
  { key: 'stayPillar', label: 'Stay feature', group: 'Home' },
  { key: 'philosophy', label: 'Philosophy / treatment room', group: 'Home' },
  { key: 'ctaBand', label: 'Call-to-action band', group: 'Home' },
  { key: 'suite', label: 'Stay page hero', group: 'Stay' },
  { key: 'carPillar', label: 'Car hire feature', group: 'Cars' },
  { key: 'roomCosy', label: 'Cosy Room', group: 'Rooms' },
  { key: 'roomStandard', label: 'Standard Room', group: 'Rooms' },
  { key: 'roomDeluxe', label: 'Deluxe Room', group: 'Rooms' },
  { key: 'lounge', label: 'Relaxation lounge', group: 'Gallery' },
  { key: 'oils', label: 'Aromatherapy oils', group: 'Gallery' },
  { key: 'facial', label: 'Glow facials', group: 'Gallery' },
  { key: 'stones', label: 'Hot stone ritual', group: 'Gallery' },
  { key: 'nails', label: 'Nail bar', group: 'Gallery' },
  { key: 'massage', label: 'Quiet corners', group: 'Gallery' },
];

/** Maps a room id (data/accommodation.ts) → its editable image key. */
export const ROOM_IMAGE_KEY: Record<string, ImageKey> = {
  cosy: 'roomCosy',
  standard: 'roomStandard',
  deluxe: 'roomDeluxe',
};
